import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import * as FeatherIcon from "react-feather";
import { Tooltip } from "react-tooltip";
import { useParams } from "react-router-dom";

const formatScorecardData = (rawData) => {
  if (!Array.isArray(rawData) || rawData.length === 0) {
    return null;
  }

  // We initialize the result object which will hold the final formatted data.
  const result = {
    company: rawData[0]?.company || "Unknown Company",
    competitor: rawData[0]?.client || "Unknown Competitor",
    analysisDate: rawData[0]?.created_at?.split("T")[0] || "Unknown Date",
    cxoTakeaway:
      "Cell Signaling Technology (CST) is a Massachusetts-based, privately held antibody and reagents specialist with ~$500M+ revenue (FY2024 est.), well regarded for premium-quality antibodies in signaling, oncology, and epigenetics. The company enjoys strong brand equity, direct scientist engagement, and rigorous validation standards. However, its relatively narrow portfolio breadth, weaker global distribution reach, and modest digital scale create vulnerabilities against Abcam’s broader catalog, high e-commerce penetration, and diversified global positioning.",
    dimensions: [],
  };

  // Now we loop through all records to collect all the dimensions
  rawData.forEach((entry) => {
    const tier1 = entry?.response?.tier1_summary || {};
    const tier2 =
      entry?.response?.tier2_drilldowns?.subdimensions ||
      entry?.response?.tier2_drilldowns?.subdimensionss ||
      [];
    const dimensionName = tier1.dimensions || "Unnamed Dimension";

    const dimension = {
      id: dimensionName.toLowerCase().replace(/\s+/g, "-"),
      name: dimensionName,
      category: "Competitive Market Standing",
      score: tier1.score_out_of_10 || 0,
      scoreText: getScoreText(tier1.score_out_of_10),
      confidence: `${tier1.confidence || "Medium"} Confidence`,
      whyScore: tier1.rationale || "N/A",
      soWhat: tier1.rationale || "N/A",
      trend: tier1.trend || "flat",
      implication_for_user_company: tier1.implication_for_user_company || "N/A",
      subdimensions: Array.isArray(tier2)
        ? tier2.map((sub) => ({
            name: formatSubdimensionName(sub?.name),
            score: sub?.score_out_of_10 || 0,
            scoreText: getScoreText(sub?.score_out_of_10),
            confidence: `${sub?.confidence || "Medium"} Confidence`,
            whyScore: sub?.rationale || "N/A",
            soWhat: sub?.rationale || "N/A",
          }))
        : [],
    };

    // Push the formatted dimension object into the `dimensions` array
    result.dimensions.push(dimension);
  });

  return result;
};

const formatSubdimensionName = (name = "") => {
  return name
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

// Utility function to determine score text
const getScoreText = (score) => {
  if (score >= 8) return "Strong";
  if (score >= 6) return "Parity";
  if (score >= 4) return "Weakness";
  return "Very Weak";
};

// Format subdimension name
// const formatSubdimensionName = (name) => {
//   return name
//     .split("_")
//     .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
//     .join(" ");
// };

// const ScorecardData = {
//   company: "Cell Signaling Technology",
//   competitor: "Abcam",
//   analysisDate: "2025-09-08",
//   cxoTakeaway:
//     "Cell Signaling Technology (CST) is a Massachusetts-based, privately held antibody and reagents specialist with ~$500M+ revenue (FY2024 est.), well regarded for premium-quality antibodies in signaling, oncology, and epigenetics. The company enjoys strong brand equity, direct scientist engagement, and rigorous validation standards. However, its relatively narrow portfolio breadth, weaker global distribution reach, and modest digital scale create vulnerabilities against Abcam’s broader catalog, high e-commerce penetration, and diversified global positioning.",
//   dimensions: [
//     {
//       id: "market-position",
//       name: "Market Position",
//       category: "Competitive Market Standing",
//       score: 6.0,
//       scoreText: "Weakness",
//       confidence: "High Confidence",
//       whyScore:
//         "CST is a premium antibody and reagents player, highly respected in academic research labs, especially for signaling pathways. However, compared with Abcam’s 110,000+ products and broader applications across diagnostics, cell therapy, and tools, CST’s portfolio breadth is narrower. CST has strong share in niches (e.g., phospho-specific antibodies) but weaker global scale.",
//       soWhat:
//         "Abcam can leverage its broad catalog, partnerships, and e-commerce reach to capture share where CST’s product coverage is thin.",
//       trend: "flat",
//       implication_for_user_company:
//         "Target CST accounts with depth-of-catalog and digital engagement; highlight Abcam’s validated kits and global logistics as differentiators.",
//       subdimensions: [
//         {
//           name: "Share Rank",
//           score: 6,
//           scoreText: "Weakness",
//           confidence: "High Confidence",
//           whyScore:
//             "CST estimated ~$500M revenue vs Abcam ~$515M (FY2024). Both are top-tier specialty antibody suppliers, but CST has narrower application focus.",
//           soWhat:
//             "Abcam’s broader scope allows greater penetration into diagnostics, CROs, and biotech accounts.",
//         },
//         {
//           name: "Geo Mix",
//           score: 5,
//           scoreText: "Weakness",
//           confidence: "High Confidence",
//           whyScore:
//             "CST has global distributors and subsidiaries (China, Europe, Japan) but remains U.S.-centric in operations and sales reliance. Abcam generates ~40% revenue from China and Europe, giving better geographic diversification.",
//           soWhat:
//             "Abcam’s international footprint reduces risk and improves customer proximity.",
//         },
//         {
//           name: "Segment Mix",
//           score: 6,
//           scoreText: "Parity",
//           confidence: "Medium Confidence",
//           whyScore:
//             "CST focuses heavily on academic/biopharma research antibodies and reagents, with growing kits and tools. Abcam is more diversified into diagnostics, biopharma services, and consumables.",
//           soWhat:
//             "Abcam’s segment diversity offers resilience and broader customer capture.",
//         },
//         {
//           name: "Customer Satisfaction",
//           score: 8,
//           scoreText: "Strong",
//           confidence: "High Confidence",
//           whyScore:
//             "CST is widely praised for antibody quality, validation, and reproducibility (often considered the gold standard by researchers). Abcam has broader catalog but sometimes mixed reproducibility feedback.",
//           soWhat:
//             "CST’s reputation is strong, but Abcam’s scale and digital ease-of-use often trump in purchasing decisions.",
//         },
//         {
//           name: "Concentration Risk",
//           score: 5,
//           scoreText: "Weakness",
//           confidence: "High Confidence",
//           whyScore:
//             "Heavy reliance on research antibodies and academic funding cycles. Limited diversification into diagnostics and therapeutic tools compared with Abcam.",
//           soWhat:
//             "Abcam’s broader applications and funding exposure make it less vulnerable to cyclical downturns.",
//         },
//       ],
//     },
//     {
//       id: "financials-strategy",
//       name: "Financials & Strategy",
//       category: "Financial Performance & Capital Allocation",
//       score: 6.2,
//       scoreText: "Weakness",
//       confidence: "Medium Confidence",
//       whyScore:
//         "Privately held with ~$500M revenue, CST shows healthy margins given premium pricing and strong brand. Growth solid but below Abcam’s double-digit pace fueled by aggressive e-commerce and partnerships.",
//       soWhat:
//         "Abcam’s scale, public-market resources, and inorganic growth engine position it more strongly for long-term capture.",
//       trend: "flat",
//       implication_for_user_company:
//         "Abcam can leverage scale advantages, stronger e-commerce, and capital access to expand faster than CST in growth geographies.",
//       subdimensions: [
//         {
//           name: "Revenue Scale",
//           score: 6,
//           scoreText: "Weakness",
//           confidence: "High Confidence",
//           whyScore:
//             "CST ~$500M revenue vs Abcam ~$515M, both mid-sized life sciences firms. CST remains private with lower disclosure and transparency.",
//           soWhat:
//             "Abcam’s global revenue streams and visibility enhance investor trust and growth leverage.",
//         },
//         {
//           name: "Growth Trend",
//           score: 6,
//           scoreText: "Parity",
//           confidence: "Medium Confidence",
//           whyScore:
//             "CST growth mid-to-high single digits, steady but not explosive. Abcam consistently delivers higher double-digit growth due to e-commerce penetration and strategic acquisitions.",
//           soWhat:
//             "Abcam’s faster growth trajectory enhances competitive positioning.",
//         },
//         {
//           name: "Profitability",
//           score: 7,
//           scoreText: "Strong",
//           confidence: "High Confidence",
//           whyScore:
//             "CST enjoys premium pricing on rigorously validated antibodies, likely delivering healthy EBITDA margins (~20–25%).",
//           soWhat:
//             "Abcam competes strongly on volume/scale but CST retains defensibility via margin strength.",
//         },
//         {
//           name: "Capital Allocation",
//           score: 5,
//           scoreText: "Weakness",
//           confidence: "Medium Confidence",
//           whyScore:
//             "As a private company, CST reinvests in antibody development and validation but lacks the aggressive M&A cadence of Abcam.",
//           soWhat:
//             "Abcam’s access to capital enables continuous platform expansion.",
//         },
//         {
//           name: "Strategic Intent",
//           score: 6,
//           scoreText: "Parity",
//           confidence: "Medium Confidence",
//           whyScore:
//             "Focused on scientific rigor and depth in signaling/oncology research. Clear strategy but narrower than Abcam’s ambition to be a global life sciences solutions provider.",
//           soWhat:
//             "Abcam’s broader strategic vision may yield higher long-term relevance.",
//         },
//       ],
//     },
//     {
//       id: "marketing-distribution",
//       name: "Marketing & Distribution",
//       category: "Go-to-Market Strategy",
//       score: 5.5,
//       scoreText: "Weakness",
//       confidence: "Medium Confidence",
//       whyScore:
//         "CST sells directly via its website and through distributors but has weaker digital reach and catalog breadth compared with Abcam’s dominant e-commerce platform. Abcam has near 80% digital sales penetration.",
//       soWhat:
//         "Abcam’s digital-first approach provides global accessibility and purchasing convenience that CST struggles to match.",
//       trend: "up",
//       implication_for_user_company:
//         "Push e-commerce strengths to capture CST customers, especially in long-tail labs and global research markets.",
//       subdimensions: [
//         {
//           name: "Channel Mix",
//           score: 5,
//           scoreText: "Weakness",
//           confidence: "High Confidence",
//           whyScore:
//             "CST uses a mix of direct sales and distributors, but Abcam dominates online direct-to-researcher purchases.",
//           soWhat:
//             "Abcam’s channel efficiency and breadth yield stronger global reach.",
//         },
//         {
//           name: "Partner Ecosystem",
//           score: 5,
//           scoreText: "Weakness",
//           confidence: "Medium Confidence",
//           whyScore:
//             "CST has collaborations with research institutes but fewer pharma/service alliances than Abcam.",
//           soWhat:
//             "Abcam’s broad partnerships reinforce credibility and open new revenue streams.",
//         },
//         {
//           name: "Brand Strength",
//           score: 8,
//           scoreText: "Strong",
//           confidence: "High Confidence",
//           whyScore:
//             "CST brand highly regarded for antibody validation and reproducibility; consistently cited in high-impact journals.",
//           soWhat:
//             "Abcam’s global brand breadth may outweigh CST’s premium scientific reputation in procurement decisions.",
//         },
//         {
//           name: "Pricing Power",
//           score: 7,
//           scoreText: "Strong",
//           confidence: "High Confidence",
//           whyScore:
//             "CST charges premium pricing due to validation depth; customers pay for reliability. Abcam offers competitive range across quality/price tiers.",
//           soWhat:
//             "Abcam can undercut in cost-sensitive segments while CST retains premium niches.",
//         },
//         {
//           name: "Digital Reach",
//           score: 4,
//           scoreText: "Weakness",
//           confidence: "High Confidence",
//           whyScore:
//             "Website functional but limited SEO and smaller catalog. Abcam commands dominant online visibility with stronger traffic and citation reach.",
//           soWhat: "Digital dominance is Abcam’s strongest advantage over CST.",
//         },
//       ],
//     },
//     {
//       id: "product-innovation",
//       name: "Product & Innovation",
//       category: "R&D and Innovation",
//       score: 6.5,
//       scoreText: "Parity",
//       confidence: "High Confidence",
//       whyScore:
//         "CST is science-led, highly rigorous, and recognized for high-quality, validated antibodies. However, it lacks the scale of Abcam’s kits, recombinant antibody expansion, and diagnostic/therapeutic adjacency plays.",
//       soWhat:
//         "Abcam can differentiate via breadth of catalog, recombinant portfolio, and speed-to-market, but CST remains premium in core antibodies.",
//       trend: "flat",
//       implication_for_user_company:
//         "Abcam should emphasize innovation breadth and applied research solutions to out-position CST’s narrower antibody focus.",
//       subdimensions: [
//         {
//           name: "Portfolio Breadth",
//           score: 5,
//           scoreText: "Weakness",
//           confidence: "High Confidence",
//           whyScore:
//             "CST has thousands of antibodies across signaling/oncology, but Abcam’s >110,000 catalog covers much broader areas.",
//           soWhat:
//             "Abcam’s breadth gives switching cost advantages and broader coverage.",
//         },
//         {
//           name: "R&D Intensity",
//           score: 7,
//           scoreText: "Strong",
//           confidence: "High Confidence",
//           whyScore:
//             "CST invests heavily in validation, phospho-antibody development, and cutting-edge cell biology reagents. Known for scientific rigor.",
//           soWhat:
//             "Abcam can match via recombinant R&D, but CST retains credibility in niche validation.",
//         },
//         {
//           name: "Patent Activity",
//           score: 4,
//           scoreText: "Weakness",
//           confidence: "Medium Confidence",
//           whyScore:
//             "CST files limited patents (focuses on know-how and validation standards). Abcam’s recombinant antibodies and kits have broader IP coverage.",
//           soWhat:
//             "Abcam’s IP portfolio strengthens defensibility across multiple markets.",
//         },
//         {
//           name: "Launch Velocity",
//           score: 5,
//           scoreText: "Weakness",
//           confidence: "Medium Confidence",
//           whyScore:
//             "CST launches regular antibodies and kits but cadence is modest vs Abcam’s high-volume additions to catalog.",
//           soWhat:
//             "Abcam’s launch speed reinforces digital and catalog dominance.",
//         },
//         {
//           name: "Sustainability Edge",
//           score: 5,
//           scoreText: "Parity",
//           confidence: "Medium Confidence",
//           whyScore:
//             "Both firms signal sustainability initiatives, but no standout leadership from CST. Abcam more vocal on ESG reporting as a public company.",
//           soWhat:
//             "Abcam can use ESG positioning to differentiate in enterprise procurement.",
//         },
//       ],
//     },
//     {
//       id: "organization-leadership",
//       name: "Organization & Leadership",
//       category: "Leadership and Culture",
//       score: 6.0,
//       scoreText: "Parity",
//       confidence: "Medium Confidence",
//       whyScore:
//         "CST is family-founded and scientist-driven, emphasizing rigor and academic credibility. Stable leadership but limited governance disclosure as a private company. Abcam benefits from public company governance, broader leadership bench, and visibility.",
//       soWhat:
//         "Abcam can leverage governance transparency and global leadership to appeal to institutional partners and investors.",
//       trend: "flat",
//       implication_for_user_company:
//         "Leverage governance, transparency, and global culture to out-position CST with enterprise accounts and talent.",
//       subdimensions: [
//         {
//           name: "Leadership Stability",
//           score: 7,
//           scoreText: "Strong",
//           confidence: "High Confidence",
//           whyScore:
//             "CST founded by Michael Comb; strong scientific leadership and cultural continuity. Stable, science-led decision-making.",
//           soWhat:
//             "Both companies have stable leadership, but Abcam’s governance enhances stakeholder trust.",
//         },
//         {
//           name: "Org Design",
//           score: 6,
//           scoreText: "Parity",
//           confidence: "Medium Confidence",
//           whyScore:
//             "Lean, research-driven organization; global subsidiaries but less complex than Abcam’s 1,600+ workforce across multiple hubs.",
//           soWhat:
//             "Abcam’s complexity enables global execution, CST’s agility supports focus.",
//         },
//         {
//           name: "Talent Density",
//           score: 6,
//           scoreText: "Weakness",
//           confidence: "Medium Confidence",
//           whyScore:
//             "Strong scientific staff reputation, but smaller scale limits breadth of specialized expertise compared to Abcam’s wider team.",
//           soWhat:
//             "Abcam’s larger scale provides greater specialized bench strength.",
//         },
//         {
//           name: "Governance Quality",
//           score: 5,
//           scoreText: "Weakness",
//           confidence: "High Confidence",
//           whyScore:
//             "Private company with minimal governance disclosure; contrasts with Abcam’s public company accountability.",
//           soWhat:
//             "Abcam can emphasize transparency and compliance as competitive strengths.",
//         },
//         {
//           name: "Culture Signal",
//           score: 7,
//           scoreText: "Strong",
//           confidence: "Medium Confidence",
//           whyScore:
//             "Employee reviews positive on mission-driven culture and science-led environment, though smaller scale limits perks vs public peers.",
//           soWhat:
//             "CST culture strong, but Abcam’s global recognition aids recruiting and retention.",
//         },
//       ],
//     },
//   ],
// };

// Main React Component
const ScoreCard = ({ scorecardData, setThreatScore }) => {
  // let ScorecardData = [];
  // useEffect(() => {
  const ScorecardData = formatScorecardData(scorecardData);

  useEffect(() => {
    if (scorecardData?.length > 0) {
      const averageScoreIndex = ScorecardData?.dimensions?.length || 0;
      if (averageScoreIndex > 0) {
        const totalScore = ScorecardData?.dimensions.reduce(
          (total, dimension) => {
            return total + (parseFloat(dimension?.score) || 0);
          },
          0
        );
        setThreatScore(Math.round(parseFloat(totalScore / averageScoreIndex)));
      }
    }
  }, [scorecardData]);

  // }, [scorecardData]);

  const [expandedDimension, setExpandedDimension] = useState(null);
  const { clientName, competitorName } = useParams();
  const toggleDimension = (dimensionId) => {
    setExpandedDimension(
      expandedDimension === dimensionId ? null : dimensionId
    );
  };

  // console.log("SCORECARD DATA: " + JSON.stringify(scorecardData));

  const getScoreClass = (score) => {
    if (score >= 9) return "btn-success"; // Leadership - Dark Green
    if (score >= 7) return "btn-success"; // Strong - Green
    if (score >= 5) return "btn-warning"; // Parity - Yellow
    if (score >= 3) return "btn-danger"; // Weakness - Red
    return "btn-danger"; // Severe Risk - Dark Red
  };

  const getScoreBgClass = (score) => {
    if (score >= 9) return "bg-success"; // Leadership - Dark Green
    if (score >= 7) return "bg-success"; // Strong - Green
    if (score >= 5) return "bg-warning"; // Parity - Yellow
    if (score >= 3) return "bg-danger"; // Weakness - Red
    return "bg-danger"; // Severe Risk - Dark Red
  };

  const getConfidenceClass = (confidence) => {
    const level = confidence.toLowerCase();
    if (level.includes("high")) return "badge bg-success";
    if (level.includes("medium")) return "badge bg-warning";
    return "badge bg-danger";
  };

  const formatText = (text) => {
    return text.replace(
      /\*\*(.*?)\*\*/g,
      '<span class="bg-warning bg-opacity-25 px-1 rounded fw-semibold">$1</span>'
    );
  };

  const getClassName = (score) => {
    if (score >= 9) return "green";
    if (score >= 7) return "green-light";
    if (score >= 5) return "yellow";
    if (score >= 3) return "orange";
    if (score >= 0) return "red";
    return "";
  };

  const customStyles = `
    .score-badge {
      width: auto;
      margin-right: 5px;
      border-radius: 50%;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-weight: 500;
      font-size: 13px;
      color: #363b4b;
    }
    
    .score-badge-sm {
      // width: 15px;
      border-radius: 50%;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-weight: 500;
      font-size: 12px;
      color: #363b4b;
    }

    .expand-icon {
      transition: transform 0.3s ease;
      cursor: pointer;
    }

    .expanded .expand-icon {
      transform: rotate(90deg);
    }

    .dimension-row:hover {
      background-color: #f8f9fa !important;
    }

    .leadership-score { background-color: #198754 !important; }
    .strong-score { background-color: #20c997 !important; }
    .parity-score { background-color: #ffc107 !important; }
    .weakness-score { background-color: #dc3545 !important; }
    .severe-score { background-color: #842029 !important; }


    .legend-container {
      display: flex;
      justify-content: flex-start;
      gap: 20px; /* Add space between each legend item */
      align-items: center;
      margin-bottom: 10px;
    }
    
    .legend-item {
      display: flex;
      align-items: center;
      font-size: 12px;
    }
    
    .color-box {
      width: 40px;
      height: 10px;
      margin-right: 8px;
      border-radius: 4px;
    }
    
    .green {
      background-color: #bdffbd;
    }
    
    .green-light {
      background-color: #d8ffd8;
    }
    
    .yellow {
      background-color: #fff9c7;
    }
    
    .orange {
      background-color: #ffdaa3;
    }
    
    .red {
      background-color: #ffb7b2;
    }
    
    
    

  `;

  return (
    <>
      <style>{customStyles}</style>
      <div>
        <div className="">
          <div className="">
            {/* Scorecard */}
            <div className="company_head_score_block">
              <h2 className="company_head_score mb-2">Company Scorecard</h2>{" "}
              {/* <p className="text-muted mb-0">
                  Competitive analysis based on comprehensive research and
                  industry benchmarking | Analysis Date:{" "}
                  {ScorecardData.analysisDate}
                </p> */}
            </div>
            {/* <span
              style={{
                fontWeight: 400,
                fontSize: "12px",
                lineHeight: "15px",
                color: "#00000099",
                marginBottom: "15px",
              }}
            >
              (9–10= Leader, 7–8 = strong, 5–6 = parity, 3–4 = Weak, 0–2 =
              Severe risk.)
            </span> */}

            {/* ++++++++++++++++++++++++++++++++++++ */}
            <div className="legend-container">
              <div className="legend-item">
                <div className="color-box green"></div>
                <span>9-10 Leader</span>
              </div>
              <div className="legend-item">
                <div className="color-box green-light"></div>
                <span>7-8 Strong</span>
              </div>
              <div className="legend-item">
                <div className="color-box yellow"></div>
                <span>5-6 Parity</span>
              </div>
              <div className="legend-item">
                <div className="color-box orange"></div>
                <span>3-4 Weak</span>
              </div>
              <div className="legend-item">
                <div className="color-box red"></div>
                <span>0-2 Severe Risk</span>
              </div>
            </div>
            {/* ++++++++++++++++++++++++++++++++++++ */}

            <div className="">
              <div className="table-responsive">
                <table className="custom-resizable-table">
                  <thead className="table-primary">
                    <tr className="custom-resizable-table-header">
                      <th className="">
                        <div className="custom-resizable-table-th-content">
                          Dimension Name{" "}
                        </div>
                      </th>
                      <th className="">
                        <div className="custom-resizable-table-th-content">
                          Score{" "}
                        </div>
                      </th>
                      <th className="">
                        <div className="custom-resizable-table-th-content">
                          Why This Score
                        </div>
                      </th>
                      {/* <th className="">
                        <div className="custom-resizable-table-th-content">
                          {" "}
                          Implication for {clientName ?? "H.B. Fuller"}
                        </div>
                      </th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {ScorecardData?.dimensions.map((dimension) => (
                      <React.Fragment key={dimension?.id}>
                        {/* Main Dimension Row */}
                        <tr
                          className={`dimension-row cursor-pointer gtr-row ${
                            expandedDimension === dimension?.id
                              ? "table-light"
                              : ""
                          }`}
                          onClick={() => toggleDimension(dimension?.id)}
                        >
                          <td className="gtr-cell">
                            <div
                              className={`gtr-cell-content-container justify-content-start align-items-baseline ${
                                expandedDimension === dimension?.id
                                  ? "expanded"
                                  : ""
                              }`}
                            >
                              <span className="expand-icon ms-2 fs-6 mr-2">
                                <FeatherIcon.ChevronRight
                                  width="18"
                                  height="18"
                                />
                              </span>
                              <div>
                                <p className="mb-1">{dimension?.name}</p>
                                <p className="mb-1">
                                  {" "}
                                  <small className="text-muted">
                                    {dimension?.category}
                                  </small>
                                </p>
                              </div>
                            </div>
                          </td>
                          <td
                            className={`gtr-cell ${getClassName(
                              dimension?.score
                            )} text-center`}
                            style={{ minWidth: "170px" }}
                          >
                            {/* <div className="gtr-cell-content-container justify-content-start justify-content-start"> */}
                            <div className="gtr-cell-content-container justify-content-start justify-content-center">
                              <p className="score-badge mb-0">
                                {dimension?.score}{" "}
                              </p>
                              <span
                                data-tooltip-id={`tooltip-${dimension?.id}`}
                                data-tooltip-html={`
                                  <div class='text-start'>
                                    <div class=''>${dimension?.scoreText}</div>
                                    <div class='${getConfidenceClass(
                                      dimension?.confidence
                                    )}'>${dimension?.confidence}</div>
                                  </div>
                                `}
                                className="cursor-pointer"
                                style={{ position: "relative", zIndex: "1" }}
                              >
                                {/* <FeatherIcon.Info width="14" height="14" /> */}
                              </span>

                              <Tooltip
                                id={`tooltip-${dimension?.id}`}
                                place="top"
                                className="custom-tooltip"
                                // style={{ height: "auto", width: "auto" }}
                              />
                            </div>

                            {/* Optional: remove these if they should only appear in tooltip */}
                          </td>
                          <td
                            className="gtr-cell"
                            style={{ maxWidth: "300px" }}
                          >
                            <div
                              className="gtr-cell-content-container d-block"
                              dangerouslySetInnerHTML={{
                                __html: formatText(dimension.whyScore),
                              }}
                            />
                          </td>
                          {/* <td
                            className="gtr-cell"
                            style={{ maxWidth: "350px" }}
                          >
                            <div className="gtr-cell-content-container d-block">
                              {dimension.soWhat}
                            </div>
                          </td> */}
                        </tr>

                        {/* Sub-dimension Rows */}
                        {expandedDimension === dimension.id &&
                          dimension.subdimensions?.map((sub, index) => (
                            <tr
                              key={index}
                              className="dimension-row cursor-pointer gtr-row table-light"
                            >
                              <td
                                className="gtr-cell"
                                style={{ paddingLeft: "3rem" }}
                              >
                                <div
                                  className="text-secondary"
                                  style={{
                                    fontSize: "13px",
                                    fontweight: "600",
                                  }}
                                >
                                  {sub.name === "R And D Intensity"
                                    ? "R&D Intensity"
                                    : sub.name}
                                </div>
                              </td>
                              <td
                                className={`gtr-cell text-center  ${getClassName(
                                  sub.score
                                )}`}
                              >
                                <div
                                  className="score-badge-sm"
                                  // className={`score-badge-sm ${
                                  //   sub.score >= 9
                                  //     ? "leadership-score"
                                  //     : sub.score >= 7
                                  //     ? "strong-score"
                                  //     : sub.score >= 5
                                  //     ? "parity-score"
                                  //     : sub.score >= 3
                                  //     ? "weakness-score"
                                  //     : "severe-score"
                                  // }`}
                                >
                                  {sub.score}
                                </div>
                                {/* <div className="">{sub.scoreText}</div>
                                <div
                                  className={`badge ${
                                    getConfidenceClass(sub.confidence).split(
                                      " "
                                    )[1]
                                  } ${
                                    getConfidenceClass(sub.confidence).split(
                                      " "
                                    )[2]
                                  }`}
                                  // style={{ fontSize: "0.7rem" }}
                                >
                                  {sub.confidence}
                                </div> */}
                              </td>
                              <td
                                className="gtr-cell"
                                style={{
                                  maxWidth: "300px",
                                  fontSize: "12px",
                                }}
                              >
                                <div className="tooltip-container gtr-cell-content-container d-block">
                                  <div
                                    className="mb-2"
                                    dangerouslySetInnerHTML={{
                                      __html: formatText(sub.whyScore),
                                    }}
                                  />
                                  {sub.whyScoreSources?.length > 0 && (
                                    <div
                                      className="custom-tooltip"
                                      style={{
                                        boxShadow: "unset",
                                        fontSize: "12px",
                                      }}
                                    >
                                      <span className="tooltip-title">
                                        📊 Data Sources & Reasoning
                                      </span>
                                      <div className="tooltip-content">
                                        {sub.whyScoreSources}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td>
                              {/* <td
                                className="gtr-cell"
                                style={{ maxWidth: "350px", fontSize: "12px" }}
                              >
                                <div className="tooltip-container gtr-cell-content-container d-block">
                                  <div className="mb-2">{sub.soWhat}</div>
                                  {sub.soWhatBasis?.length > 0 && (
                                    <div className="custom-tooltip">
                                      <span className="tooltip-title">
                                        🎯 Strategic Reasoning
                                      </span>
                                      <div className="tooltip-content">
                                        {sub.soWhatBasis}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td> */}
                            </tr>
                          ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ScoreCard;
